import React, { useEffect, useReducer, useState, useRef, forwardRef } from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';
import { initializeFirebase } from '../shared/firebase';
import AssetPreview from '../shared/asset-preview';
import AssetLibrary from '../shared/asset-library';
import { debounce } from '../shared/browser-utils';
import './dam.css';
import { ALL_ENTITY_KINDS } from '../shared/world';
import Icon from '../shared/icon';

initializeFirebase();

const DotsIcon = forwardRef(({ onClick, enabled }, ref) => (
  <svg ref={ref} width="24" height="24" viewBox="0 0 24 24" onClick={() => enabled && onClick()} style={{ opacity: enabled ? 1.0 : 0.2 }}>
    <circle cx="12" cy="5" r="2.5" fill="#718096" />
    <circle cx="12" cy="12" r="2.5" fill="#718096" />
    <circle cx="12" cy="19" r="2.5" fill="#718096" />
  </svg>
));

// function AssetProp({ name, value, onChange }) {
//   return (
//     <div className="dam__row dam__prop">
//       <label>{name}</label>
//       <input type="text" value={value} onChange={(e) => onChange(e.target.value)} />
//     </div>
//   );
// }

function IntProp({ label, value, onChange }) {
  const _onChange = (e) => {
    const v = parseInt(e.target.value);
    if (!isNaN(v)) {
      onChange(v);
    } else {
      onChange(0);
    }
  };
  return (
    <div className="dam__row dam__prop">
      <label>{label}</label>
      <input type="text" value={value} onChange={_onChange} />
    </div>
  );
}

function ChoiceProp({ label, options, value, onChange }) {
  return (
    <div className="dam__row dam__prop">
      <label>{label}</label>
      <select value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

function Modal({ positionRef, children, onCancel }) {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    window.addEventListener('resize', forceUpdate);
    return () => window.removeEventListener('resize', forceUpdate);
  });
  let x = window.innerWidth / 2;
  let y = window.innerHeight / 2;
  if (positionRef.current) {
    const bounds = positionRef.current.getBoundingClientRect();
    x = bounds.x;
    y = bounds.y;
  }

  return (
    <>
      <div className="modal__backdrop" onClick={onCancel}></div>
      <div className="modal__pane" style={{ left: `${x}px`, top: `${y}px` }}>
        {children}
      </div>
    </>
  );
}

function Popup({ options, positionRef, onChoose, onCancel }) {
  return (
    <Modal positionRef={positionRef} onCancel={onCancel}>
      <div className="popup">
        {options.map((option) => (
          <div key={option} className="popup__row" onClick={() => onChoose(option)}>
            {option}
          </div>
        ))}
      </div>
    </Modal>
  );
}

const SAVE_STATE_IDLE = '';
const SAVE_STATE_SAVING = 'Saving...';
const SAVE_STATE_SAVED = 'Saved';
//const SAVE_STATE_ERROR = 'Save Error';

const ACTION_UPLOAD_GLB = 'Upload .glb file';
const ACTION_DELETE_ASSET = 'Delete asset';

const UPLOAD_STATE_IDLE = 'idle';
const UPLOAD_STATE_UPLOADING = 'uploading';
const UPLOAD_STATE_ERROR = 'error';

export default function DAM() {
  const [user, setUser] = useState();
  const [assetLibraries, setAssetLibraries] = useState();
  const [activeAssetLibraryName, setActiveAssetLibraryName] = useState();
  const [activeAssetName, setActiveAssetName] = useState();
  const activeAssetLibrary = assetLibraries && assetLibraries.find((l) => l.name === activeAssetLibraryName);
  const activeAsset = activeAssetLibrary && activeAssetLibrary.assets.find((a) => a.name === activeAssetName);
  const [assetLibraryMenuVisible, setAssetLibraryMenuVisible] = useState(false);
  const assetLibraryMenuButton = useRef(null);
  const assetMenuButton = useRef(null);
  const [assetMenuVisible, setAssetMenuVisible] = useState(false);
  const [saveState, setSaveState] = useState(SAVE_STATE_IDLE);
  const [uploadState, setUploadState] = useState(UPLOAD_STATE_IDLE);
  const [uploadProgress, setUploadProgress] = useState(0.0);
  const [errorMessage, setErrorMessage] = useState('');
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const db = firebase.firestore();
    db.collection('asset-libraries')
      .get()
      .then(async (snap) => {
        //const assetLibraries = [];
        const promises = [];
        snap.forEach((doc) => {
          const json = JSON.parse(doc.data().json);
          // This creates an async function for each library and immediately executes it, returning a Promise.
          promises.push((async () => await AssetLibrary.fromJSON(json))());
        });
        const assetLibraries = await Promise.all(promises);
        setAssetLibraries(assetLibraries);
      });

    firebase.auth().onAuthStateChanged(() => {
      setUser(firebase.auth().currentUser);
    });
  }, []);

  const _saveAssetLibrary = debounce(async () => {
    if (!activeAssetLibraryName) return;
    const json = JSON.stringify(activeAssetLibrary.toJSON());
    await firebase.firestore().collection('asset-libraries').doc(activeAssetLibraryName).set({ json });
    setSaveState(SAVE_STATE_SAVED);
  }, 2000);

  const saveAssetLibrary = () => {
    if (!user) return;
    setSaveState(SAVE_STATE_SAVING);
    _saveAssetLibrary();
  };

  const onCreateAssetLibrary = () => {
    let assetLibraryName = prompt('New asset library name:');
    if (!assetLibraryName) return;
    if (!AssetLibrary.isValidName(assetLibraryName)) {
      alert(`"${assetLibraryName}" is not a valid name. Please only use lowercase letters.`);
      return;
    }
    if (assetLibraries.find((l) => l.name === assetLibraryName)) {
      alert(`"${assetLibraryName}" already exists. Please choose a unique name.`);
      return;
    }
    const assetLibrary = new AssetLibrary();
    assetLibrary.name = assetLibraryName;
    assetLibraries.push(assetLibrary);
    setAssetLibraries(assetLibraries);
    setActiveAssetLibraryName(assetLibrary.name);
    saveAssetLibrary();
  };

  const onChangeAssetProp = (key, value) => {
    activeAsset[key] = value;
    forceUpdate();
    saveAssetLibrary();
  };
  const showAssetLibraryMenu = () => {
    setAssetLibraryMenuVisible(true);
  };

  const showAssetMenu = () => {
    setAssetMenuVisible(true);
  };

  const performAssetLibraryAction = (action) => {
    setAssetLibraryMenuVisible(false);
    if (action === ACTION_UPLOAD_GLB) {
      fileInputRef.current.click();
    }
  };

  const performAssetAction = (action) => {
    setAssetMenuVisible(false);
    if (action === ACTION_DELETE_ASSET) {
      if (window.confirm(`Are you sure you want to delete ${activeAsset.name}?`)) {
        activeAssetLibrary.deleteAssetByName(activeAsset.name);
        forceUpdate();
        saveAssetLibrary();
      }
    }
  };

  const onSelectFile = (e) => {
    const file = e.target.files[0];
    const filename = `assets/${file.name}`;
    const fileExt = filename.substring(filename.lastIndexOf('.') + 1).toLowerCase();
    if (fileExt !== 'glb') {
      setUploadState(UPLOAD_STATE_ERROR);
      setErrorMessage('Invalid file type.');
      return;
    }
    setUploadState(UPLOAD_STATE_UPLOADING);
    setUploadProgress(0.0);
    const storageRef = firebase.storage().ref(filename);
    const task = storageRef.put(file);
    task.on(
      'state_changed',
      (snapshot) => {
        setUploadProgress(snapshot.bytesTransferred / snapshot.totalBytes);
      },
      (error) => {
        setUploadState(UPLOAD_STATE_ERROR);
        setErrorMessage(`Error ${error.code}.`);
      },
      () => {
        task.snapshot.ref.getDownloadURL().then((url) => {
          setUploadState(UPLOAD_STATE_IDLE);
          onFileUploaded(filename, url);
        });
      }
    );
  };

  const onFileUploaded = async (filename, url) => {
    console.log(filename, url);
    const newAssetLibrary = await AssetLibrary.fromGLB(url);
    activeAssetLibrary.merge(newAssetLibrary);
    saveAssetLibrary();
    forceUpdate();
  };

  if (!assetLibraries)
    return (
      <div className="dam">
        <div className="dam__loading">Loading...</div>
      </div>
    );

  return (
    <div className="dam__wrapper">
      <div className="dam">
        <header className="dam__header">
          <span className="dam__save-state">{saveState || '-'}</span>
        </header>
        <div className="dam__database">
          {/* ASSET LIBRARIES */}
          <div className="dam__panel dam__libraries">
            <div className="dam__panel-header dam__row dam__text">Asset Libraries</div>
            <button onClick={onCreateAssetLibrary} className="dam__row dam__create">
              <Icon name="plus" />
              Create asset library
            </button>
            <div className="dam__scroll">
              {assetLibraries.map((library) => (
                <div
                  key={library.name}
                  className={`dam__row dam__clickable dam__text ${activeAssetLibraryName === library.name ? 'dam__row--active' : ''}`}
                  onClick={() => setActiveAssetLibraryName(library.name)}
                >
                  {library.name}
                </div>
              ))}
            </div>
          </div>
          {/* ASSETS */}
          <div className="dam__panel dam__assets">
            {uploadState === UPLOAD_STATE_IDLE && (
              <div className="dam__panel-header dam__row dam__text">
                <span>{activeAssetLibraryName || 'Assets'}</span>
                <span style={{ display: 'flex' }}>
                  <DotsIcon ref={assetLibraryMenuButton} onClick={showAssetLibraryMenu} enabled={activeAssetLibrary && user} />
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={onSelectFile} />
                </span>
              </div>
            )}
            {uploadState === UPLOAD_STATE_UPLOADING && (
              <div className="dam__panel-header dam__row dam__text">
                <div className="dam__progress" style={{ width: `${uploadProgress * 100}%` }} />
                <div className="outliner__indent-2 outliner__text">Uploading...</div>
              </div>
            )}
            {uploadState === UPLOAD_STATE_ERROR && (
              <div className="dam__panel-header dam__row dam__text dam__error">
                <div className="">{errorMessage}</div>
                <div className="dam__actions">
                  <button onClick={() => setUploadState(UPLOAD_STATE_IDLE)}>OK</button>
                </div>
              </div>
            )}
            {activeAssetLibrary && (
              <>
                <div className="dam__row dam__file-row ">
                  <a className="dam__file dam__inset2" href={activeAssetLibrary.url}>
                    {activeAssetLibrary.filename}
                  </a>
                </div>
                <div className="dam__scroll">
                  {activeAssetLibrary.assets.map((asset) => (
                    <div
                      key={asset.name}
                      className={`dam__row dam__clickable dam__text ${activeAssetName === asset.name ? 'dam__row--active' : ''} ${
                        asset.name.includes('@') ? 'dam__row-child' : ''
                      }`}
                      onClick={() => setActiveAssetName(asset.name)}
                    >
                      <Icon className="dam__asset-icon" name={asset.kind} />
                      <span>{asset.name}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* PROPERTIES */}
          <div className="dam__panel dam__properties">
            <div className="dam__panel-header dam__row dam__text">
              <span>{activeAssetName || 'Properties'}</span>
              <DotsIcon ref={assetMenuButton} onClick={showAssetMenu} enabled={activeAsset && user} />
            </div>
            {activeAsset && (
              <>
                <AssetPreview object={activeAsset.mesh} />
                <ChoiceProp
                  label="Kind"
                  options={ALL_ENTITY_KINDS}
                  value={activeAsset.kind}
                  onChange={(value) => onChangeAssetProp('kind', value)}
                />
                <IntProp label="Width" value={activeAsset.width} onChange={(value) => onChangeAssetProp('width', value)} />
                <IntProp label="Height" value={activeAsset.height} onChange={(value) => onChangeAssetProp('height', value)} />
                {activeAsset && activeAsset.kind === 'road' && (
                  <>
                    <IntProp
                      label="Pedestrians"
                      value={activeAsset.pedestrians}
                      onChange={(value) => onChangeAssetProp('pedestrians', value)}
                    />
                    <IntProp label="Bikes" value={activeAsset.bikes} onChange={(value) => onChangeAssetProp('bikes', value)} />
                    <IntProp
                      label="Cargo Bikes"
                      value={activeAsset.cargoBikes}
                      onChange={(value) => onChangeAssetProp('cargoBikes', value)}
                    />
                    <IntProp label="Cars" value={activeAsset.cars} onChange={(value) => onChangeAssetProp('cars', value)} />
                    <IntProp label="Trucks" value={activeAsset.trucks} onChange={(value) => onChangeAssetProp('trucks', value)} />
                    <IntProp label="Buses" value={activeAsset.buses} onChange={(value) => onChangeAssetProp('buses', value)} />
                    <IntProp
                      label="Passenger Trains"
                      value={activeAsset.passengerTrains}
                      onChange={(value) => onChangeAssetProp('passengerTrains', value)}
                    />
                    <IntProp
                      label="Freight Trains"
                      value={activeAsset.freightTrains}
                      onChange={(value) => onChangeAssetProp('freightTrains', value)}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {assetLibraryMenuVisible && (
        <Popup
          options={[ACTION_UPLOAD_GLB]}
          positionRef={assetLibraryMenuButton}
          onChoose={performAssetLibraryAction}
          onCancel={() => setAssetLibraryMenuVisible(false)}
        />
      )}
      {assetMenuVisible && (
        <Popup
          options={[ACTION_DELETE_ASSET]}
          positionRef={assetMenuButton}
          onChoose={performAssetAction}
          onCancel={() => setAssetMenuVisible(false)}
        />
      )}
    </div>
  );
}
